import { generic, type } from 'arktype';

/**
 * Entero 1-100 que además acepta su representación en cadena (`"10"`): los parámetros de
 * query de Express llegan siempre como string, y este es el único punto del contrato que
 * los recibe, así que la coerción vive aquí en vez de repetirse en cada controlador (mismo
 * precedente que `PORT` en `core/config/env.schema.ts`). `.or()` prueba primero la rama
 * numérica y, si no es un número, la rama de cadena (`string.integer.parse`); ambas ramas
 * comparten el mismo rango, así que un `"200"` fuera de rango se rechaza igual que un `200`
 * numérico — a diferencia de una unión declarada en una sola cadena ArkType, donde cada
 * rama valida por separado y el rango de la rama numérica no alcanzaría a la cadena.
 */
const LIMITE_POR_DEFECTO = 20;
const limitInRange = type('1 <= number.integer <= 100');
const limitFromString = type('string.integer.parse').to(limitInRange);
const limitField = limitInRange.or(limitFromString).default(LIMITE_POR_DEFECTO);

/**
 * Paginación por cursor, común a toda lista del sistema. `cursor` es opaco para el
 * cliente: se recibe tal cual lo devolvió el servidor en `nextCursor` y se reenvía sin
 * interpretarlo.
 */
export const paginationSchema = type({
	'cursor?': 'string',
	// Entero positivo; por defecto 20, máximo 100. Ver `limitField` para la coerción string→number.
	limit: limitField,
}).onUndeclaredKey('delete');

export type Pagination = typeof paginationSchema.infer;

/**
 * Construye el esquema de una respuesta paginada `{ items, nextCursor? }` a partir del
 * esquema de un elemento del catálogo. Cada dominio la reutiliza en vez de redefinir la
 * forma de la paginación.
 *
 * Se define con el generic nativo de ArkType (`generic(...)`) en vez de una función TS
 * genérica que envuelve `type({ items: itemSchema.array(), ... })`: con un parámetro de
 * tipo TS sin acotar, `itemSchema.array()` no se resuelve a un array concreto en tiempo de
 * compilación — el tipo inferido de `items` arrastra el marcador interno de ArkType
 * `" anyOrNever"` (de la distillación condicional de morphs/objetos de ArkType, que con un
 * genérico "naked" queda diferida y nunca se resuelve), así que ningún objeto literal lo
 * satisface sin `as`. El generic de ArkType sustituye `item` dentro de su propio DSL antes
 * de que TypeScript infiera el tipo de retorno, así que la sustitución llega siempre
 * resuelta a un tipo concreto. `'+': 'delete'` expresa `onUndeclaredKey('delete')` dentro
 * de la propia definición del generic (no se puede encadenar `.onUndeclaredKey` sobre una
 * definición aún no instanciada).
 */
export const createPaginatedResponseSchema = generic('item')({
	'+': 'delete',
	items: 'item[]',
	// Ausente cuando no quedan más páginas.
	'nextCursor?': 'string',
});
